import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import _merge from 'lodash/merge';
import querystring from 'query-string';
import { IS_IFRAME_MODE } from 'config/envVariable';
import { setI18nLanguage } from 'locale/i18n';
import * as clientErrTypes from 'config/clientErrorTypes';
import { initTagManager } from 'client/utils/tracking';
import store from 'client/redux/store';
import { initAppState } from 'client/redux/store';
import { setInited, setErrorOnInit, setUserStateInited } from 'client/redux/modules/flags';
import { setLoginId } from 'client/redux/modules/userLoginState';
import {
    useRemoveUnnecessaryQueryStr,
    validateTestCode
} from 'client/hooks/useInitPromo/handleQueryStringTest';
import getPromoInfoData from 'client/hooks/useInitPromo/getPromoInfoData';
import getPromoState from 'client/hooks/useInitPromo/getPromoState';
import getUserLoginState from 'client/hooks/useInitPromo/userLoginState/getUserLoginState';
import getUserLoginStateAfterEntry from 'client/hooks/useInitPromo/userLoginState/getUserLoginStateAfterEntry';
import getUserEntryState from 'client/hooks/useInitPromo/userEntryState/getUserEntryState';
import { errorActionHandler } from 'client/hooks/useClientErrorHandler';
import useHistoryPathPush from 'client/hooks/useHistoryPathPush';
import useInitAbortHelper from 'client/hooks/useInitAbortHelper';
import { getLoginUserInfo } from 'client/utils/login';
import { getLocalStorage, deleteLocalStorage } from 'utils/localStorage';
import { checkReferralCodeApi, trackCampaignClick } from 'api/client';
import Cookies from 'js-cookie';
import getMgmFlags from 'utils/getMgmFlags';
import { setGameResultRecordsAndWinnerList } from './getGameResultRecordsAndWinnerList';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

function useInitPromo(paramsPromoUUID, paramsPromoId) {
    const dispatch = useDispatch();
    const history = useHistoryPathPush();
    const location = useLocation();
    const removeUnnecessaryQueryStr = useRemoveUnnecessaryQueryStr();
    const abortHelper = useInitAbortHelper();
    function toFirstPage() {
        history.push({ pathname: `/${paramsPromoUUID}/${paramsPromoId}/join` });
    }
    function toNotFound() {
        history.push({ pathname: `/notfound` });
    }
    async function initPromo(recaptchaToken) {
        if (isNotValidPromoUUID(paramsPromoUUID)) return toNotFound();

        if (!Boolean(location?.pathname?.match(joinRegex))) toFirstPage();

        let state = {};

        function mergeAndDispatchState(source) {
            state = _merge({}, state, source);
            dispatch(initAppState(source));
        }

        try {
            const promoInfo = await getPromoInfoData(paramsPromoUUID);
            const hasRelatedTwinsPromo = promoInfo.relatedTwinsPromo !== undefined;
            const gameType = promoInfo.promo.type;

            const { isMgmShare, isMgmMission, isMgmSelf } = getMgmFlags(promoInfo?.mgmRelation);
            mergeAndDispatchState({
                flags: { isMgmShare, isMgmMission, isMgmSelf }
            });

            store.injectGameReducer(gameType, hasRelatedTwinsPromo, isMgmSelf);
            await setupPromoApp(promoInfo);
            mergeAndDispatchState(promoInfo);

            const { rest: qs, retrieved } = removeUnnecessaryQueryStr(state);
            mergeAndDispatchState({
                appState: { hasTryLiff: Boolean(retrieved.tryliff) }
            });

            retrieveStateFromLocalStorage(mergeAndDispatchState);

            const promoState = getPromoState(state, qs);

            mergeAndDispatchState({ promoState });
            dispatch(setInited());

            const { pcode = '' } = querystring.parse(window.location.search);
            // story-mention 機器人 在 IG 聊天室 發帶有 pcode 的 promo link 給 user，User 點擊 link 的次數需要被追蹤
            if (
                promoState?.enablePasscodeVerify &&
                promoInfo?.settingPromo?.enableCampaignToolPasscode &&
                pcode
            ) {
                await trackCampaignClick({ pcode });
            }

            await iframeModeBodyLazyInViewResolver();

            await validateTestCode(state, qs);
            // 取得邀請碼
            await setReferralCode(state, mergeAndDispatchState);

            const userLoginState = await getUserLoginState(state, gameType);
            mergeAndDispatchState({ userLoginState });
            if (userLoginState.isLogin) {
                await setGameResultRecordsAndWinnerList(state, mergeAndDispatchState);
                if (!userLoginState?.currentLoginID) {
                    const loginUserInfo = await getLoginUserInfo(state?.settingPromo?.loginMethod);
                    dispatch(setLoginId(loginUserInfo?.id));
                } else {
                    dispatch(setLoginId(userLoginState?.currentLoginID));
                }
                if (
                    state?.mgmRelation?.mgmRole === 'mgmShare' ||
                    state?.mgmRelation?.mgmRole === 'mgmSelf'
                ) {
                    await setGameResultRecordsAndWinnerList(state, mergeAndDispatchState, true);
                }

                const userEntryState = await getUserEntryState({
                    state,
                    dispatch,
                    abortHelper,
                    recaptchaToken,
                    enableQualificationPasscode:
                        promoInfo?.settingPromo?.enableQualificationPasscode,
                    enableReCaptcha: promoInfo?.settingPromo?.enableReCaptcha
                });
                mergeAndDispatchState({ userEntryState });
                // 登入後且跑過 Entry 流程後的檢查
                await getUserLoginStateAfterEntry(state, mergeAndDispatchState);
            } else {
                mergeAndDispatchState({ userEntryState: { shouldHideUserStateLoading: true } });
            }

            await store.initGameReducer(gameType);
            await store.initMgmSelfReducer(isMgmSelf);

            dispatch(setUserStateInited());
        } catch (error) {
            if (await abortHelper.current.canContinue()) {
                handleInitError({
                    error,
                    dispatch,
                    toNotFound,
                    isTestMode: state?.promoState?.isTestMode
                });
            }
        }
    }
    useEffect(() => {
        initPromo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return recaptchaToken => initPromo(recaptchaToken);
}

export default useInitPromo;

export const joinRegex = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9]+\/join/gm;

export function handleInitError({ dispatch, error, toNotFound = () => {}, isTestMode }) {
    dispatch(setErrorOnInit());
    if (error.extraInfo?.errType === clientErrTypes.NOT_FOUND) {
        toNotFound();
    } else {
        errorActionHandler(error, { dispatch, isTestMode });
    }
}
export async function setupPromoApp(state) {
    const {
        settingPromo: { language }
    } = state;

    setSiteTitle(state);
    setSiteFavicon(state);
    document.documentElement.setAttribute('lang', language);
    await setI18nLanguage({ lang: language });
    initTagManager(state);
}
export function setSiteTitle(state) {
    const {
        settingPromo: { metaTitle },
        promo: { name }
    } = state;
    document.title = metaTitle || name;
}
export function setSiteFavicon(state) {
    const {
        sponsor: { favIconUrl }
    } = state;
    const selectorFavicon = document.querySelector("link[rel='shortcut icon']");
    if (selectorFavicon !== null && Boolean(favIconUrl)) {
        selectorFavicon.href = favIconUrl;
    }
}
function iframeModeBodyLazyInViewResolver() {
    return new Promise(res => {
        if (IS_IFRAME_MODE && window.IntersectionObserver) {
            const observer = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.target === document.body && entry.isIntersecting) {
                        observer.unobserve(document.body);
                        res(true);
                    }
                });
            });

            observer.observe(document.body);
        } else {
            res(true);
        }
    });
}
function isNotValidPromoUUID(promoUUID) {
    return /^\d+$/.test(promoUUID);
}
export function retrieveStateFromLocalStorage(mergeAndDispatchState) {
    //確認是不是第一次通過loginsever
    const isFirstPassLoginSever = getLocalStorage({ key: 'isfirstPassLoginSever' });
    deleteLocalStorage({ key: 'isfirstPassLoginSever' });

    mergeAndDispatchState({
        promoState: { isfirstPassLoginSever: Boolean(isFirstPassLoginSever) }
    });
}

export async function setReferralCode(state, mergeAndDispatchState) {
    // 檢查邀請碼並存入 promoState
    const {
        promo: { uuid: promoUuid },
        flags: { isMgmMission, isMgmSelf },
        promoState: { utmFromParent: { utm_content: parentReferralCodeByUrl = '' } = {} } = {}
    } = state;

    if (!isMgmMission && !isMgmSelf) return;

    const { utm_content: promoReferralCodeByUrl = '' } = querystring.parse(window.location.search);
    const referralCodeByUrl = parentReferralCodeByUrl || promoReferralCodeByUrl;
    const referralCodeByCookie = Cookies.get('ref_code');

    let isUrlCodeValid = false;
    if (Boolean(referralCodeByUrl)) {
        try {
            const resp = await checkReferralCodeApi({
                promoUuid,
                referralCode: referralCodeByUrl
            });
            if (resp.httpStatus === 200) {
                isUrlCodeValid = true;
                Cookies.set('ref_code', referralCodeByUrl);
                mergeAndDispatchState({
                    promoState: {
                        referralCode: referralCodeByUrl
                    }
                });
            } else {
                mergeAndDispatchState({
                    promoState: {
                        referralCode: ''
                    }
                });
            }
        } catch (err) {
            mergeAndDispatchState({
                promoState: {
                    referralCode: ''
                }
            });
        }
    }

    if (!isUrlCodeValid && Boolean(referralCodeByCookie)) {
        // URL 檢查不通過且 Cookie 有設定過
        try {
            const resp = await checkReferralCodeApi({
                promoUuid,
                referralCode: referralCodeByCookie
            });
            if (resp.httpStatus === 200) {
                Cookies.set('ref_code', referralCodeByCookie);
                mergeAndDispatchState({
                    promoState: {
                        referralCode: referralCodeByCookie
                    }
                });
            } else {
                Cookies.remove('ref_code');
                mergeAndDispatchState({
                    promoState: {
                        referralCode: ''
                    }
                });
            }
        } catch (err) {
            Cookies.remove('ref_code');
            mergeAndDispatchState({
                promoState: {
                    referralCode: ''
                }
            });
        }
    }
}
